// Utility functions for route calculations and coordinate handling

import { decode } from "@googlemaps/polyline-codec";
import type { Coordinates } from "../types/routes";

// Create cache key from coordinates (rounded to 4 decimal places for ~11m precision)
export const createRouteKey = (
  origin: Coordinates,
  destination: Coordinates
): string => {
  const [oLat, oLng] = origin.map((coord) => coord.toFixed(4));
  const [dLat, dLng] = destination.map((coord) => coord.toFixed(4));
  return `${oLat},${oLng}->${dLat},${dLng}`;
};

// Parse duration from Google format ("1234s") to readable format ("21 min")
export const parseDuration = (duration: string): string => {
  const seconds = parseInt(duration.replace("s", ""));
  const minutes = Math.round(seconds / 60);

  if (minutes < 1) return "< 1 min";
  if (minutes === 1) return "1 min";
  return `${minutes} min`;
};

// Parse distance from meters to readable format ("2.1 km" or "850 m")
export const parseDistance = (meters: number): string => {
  if (meters >= 1000) {
    const km = (meters / 1000).toFixed(1);
    return `${km} km`;
  }
  return `${meters} m`;
};

// Calculate Haversine distance for short-distance optimization
export const calculateHaversineDistance = (
  [lat1, lon1]: Coordinates,
  [lat2, lon2]: Coordinates
): number => {
  const R = 6371e3; // Earth's radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

// Validate coordinates are within valid ranges
export const validateCoordinates = (coords: Coordinates): boolean => {
  const [lat, lng] = coords;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

// Check if coordinates are in Serbia (rough bounds check)
export const isWithinSerbia = (coords: Coordinates): boolean => {
  const [lat, lng] = coords;
  // Rough bounding box for Serbia
  return lat >= 42.2 && lat <= 46.2 && lng >= 19.2 && lng <= 23.0;
};

// Format coordinates for logging (privacy-aware - rounded to 3 decimals)
export const formatCoordinatesForLogging = (coords: Coordinates): string => {
  const [lat, lng] = coords;
  return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
};

// Convert coordinates array to Google Routes API format
export const coordinatesToGoogleWaypoint = (coords: Coordinates) => ({
  waypoint: {
    location: {
      latLng: { latitude: coords[0], longitude: coords[1] },
    },
  },
});

// ===== POLYLINE UTILITIES =====

// Google's official polyline decoder with error handling
export const decodePolyline = (encoded: string): [number, number][] => {
  try {
    // Google's codec returns [lat, lng] pairs, which matches Leaflet's expected format
    const coordinates = decode(encoded, 5); // precision 5 is Google's default

    // Validate the decoded coordinates
    if (!validatePolyline(coordinates)) {
      console.error("[RouteUtils] Invalid coordinates in decoded polyline");
      return [];
    }

    return coordinates;
  } catch (error) {
    console.error("[RouteUtils] Polyline decoding failed:", error);
    return [];
  }
};

// Validate decoded polyline coordinates
export const validatePolyline = (coordinates: [number, number][]): boolean => {
  return (
    coordinates.length >= 2 &&
    coordinates.every(
      (coord) =>
        Array.isArray(coord) && coord.length === 2 && validateCoordinates(coord)
    )
  );
};

// Create polyline cache key (separate from route matrix key)
export const createPolylineKey = (
  origin: Coordinates,
  destination: Coordinates
): string => {
  return `polyline:${createRouteKey(origin, destination)}`;
};
